import React, { useMemo } from 'react';
import ShareModal from 'src/components/ui/ShareModal';
import { ResourcePermissionRole } from '../types';
import useSpecialistShare from '../hooks/useSpecialistShare';

interface SpecialistShareModalProps {
  /** The specialist ID to share */
  specialistId: string;
  /** The specialist name for display */
  specialistName: string;
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
  /** Current user's email */
  userEmail?: string;
  /** Current user's organisation name */
  organisationName?: string;
  /** Whether the current user is the owner */
  isOwner: boolean;
  /** The current user's permission level on this specialist */
  userPermissionLevel: ResourcePermissionRole;
}

/**
 * A specialized share modal for specialists that handles:
 * - Document-based sharing restrictions (personal/tenant document filters)
 * - Tenant sharing with the user's organization
 * - Individual user sharing
 *
 * Public ("Anyone") sharing is omitted. Granting an all-users share fails
 * under multi-tenant row-level security, so the control stays out of the
 * UI until that path works.
 *
 * This component encapsulates all specialist-specific sharing logic,
 * keeping the generic ShareModal clean and reusable.
 */
const SpecialistShareModal: React.FC<SpecialistShareModalProps> = ({
  specialistId,
  specialistName,
  open,
  onClose,
  userEmail,
  organisationName,
  isOwner,
  userPermissionLevel
}) => {
  // Fetch specialist shares and restrictions
  const {
    existingShares,
    groupShares,
    tenantShare,
    maxShareLevel,
    shareRestrictionReason,
    eligibleEmails,
    eligibleGroupIds,
    handleAddPeople,
    handleRemovePerson,
    handleUpdatePermission,
    handleRemoveGroupShare,
    handleShareWithTenant,
    handleUpdateTenantShare,
    handleRemoveTenantShare
  } = useSpecialistShare({
    specialistId: open ? specialistId : undefined, // Only fetch when modal is open
    permission: userPermissionLevel
  });

  // Compute acceptable permissions based on user's role
  // Users can only grant permissions up to their own level
  const acceptablePermissions = useMemo(() => {
    const basePermissions = [ResourcePermissionRole.READ, ResourcePermissionRole.WRITE];

    // Users with SHARE permission can grant up to SHARE level
    if (userPermissionLevel >= ResourcePermissionRole.SHARE) {
      basePermissions.push(ResourcePermissionRole.SHARE);
    }

    // Only owners can grant OWNER permission
    if (userPermissionLevel === ResourcePermissionRole.OWNER) {
      basePermissions.push(ResourcePermissionRole.OWNER);
    }

    return basePermissions;
  }, [userPermissionLevel]);

  // Determine sharing capabilities based on document restrictions.
  const canShareTenant = maxShareLevel === 'public' || maxShareLevel === 'tenant';
  const canShareIndividual = maxShareLevel !== 'private';

  return (
    <ShareModal
      open={open}
      onClose={onClose}
      title={`Share "${specialistName}"`}
      user_email={userEmail}
      shareType="specialist"
      currentShares={existingShares}
      currentGroupShares={groupShares}
      onRemoveGroupShare={handleRemoveGroupShare}
      tenantShare={tenantShare}
      organisationName={canShareTenant ? organisationName : undefined}
      isOwner={isOwner}
      currentUserPermission={userPermissionLevel}
      acceptablePermissions={acceptablePermissions}
      defaultPermission={ResourcePermissionRole.READ}
      // Individual sharing handlers (only if no personal documents)
      onAddPeople={canShareIndividual ? handleAddPeople : undefined}
      onRemovePerson={canShareIndividual ? handleRemovePerson : undefined}
      onUpdatePermission={canShareIndividual ? handleUpdatePermission : undefined}
      // Tenant sharing handlers (only if allowed by document restrictions)
      onShareWithOrganisation={canShareTenant ? handleShareWithTenant : undefined}
      onUpdateOrganisationPermission={canShareTenant ? handleUpdateTenantShare : undefined}
      onRemoveOrganisationShare={canShareTenant ? handleRemoveTenantShare : undefined}
      // Restriction info for display
      shareRestrictionReason={shareRestrictionReason}
      eligibleEmails={eligibleEmails}
      eligibleGroupIds={eligibleGroupIds}
    />
  );
};

export default SpecialistShareModal;
